/** All amounts are integer minor units (cents). VAT rates are basis points (2000 = 20 %). */

export const VAT_RATES_BP = [2000, 1000, 550, 210, 0] as const;
export type VatRateBp = (typeof VAT_RATES_BP)[number];
export const DEFAULT_VAT_RATE_BP: VatRateBp = 2000;

/**
 * Largest amount (and document total) accepted: 10 M€. Amounts are stored as 32-bit integers
 * (21 M€ at most), so VAT and sums stay far from the limit.
 */
export const MAX_AMOUNT_CENTS = 1_000_000_000;

/** Round half away from zero, as used for invoice amounts. */
export function roundCents(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

export function vatAmount(netCents: number, rateBp: number): number {
  return roundCents((netCents * rateBp) / 10_000);
}

export function grossFromNet(netCents: number, rateBp: number): number {
  return netCents + vatAmount(netCents, rateBp);
}

export function netFromGross(grossCents: number, rateBp: number): number {
  return roundCents((grossCents * 10_000) / (10_000 + rateBp));
}

/**
 * Parse a user-typed decimal into cents. The locale's decimal separator wins ("12,50" in French,
 * "12.50" in English); the other one is read as a thousands separator when it groups digits by three
 * ("1 200,50", "1.200,50", "1,200.50", "1,250" in English) and as a decimal point otherwise ("12.5").
 */
export function parseMoneyInput(input: string, locale: "fr" | "en" = "fr"): number | null {
  let normalized = input.replace(/[\s\u00a0\u202f]/g, "");
  const decimal = locale === "en" ? "." : ",";
  const group = locale === "en" ? "," : ".";
  const grouped = (s: string, sep: string) => new RegExp(`^-?[1-9]\\d{0,2}(\\${sep}\\d{3})+$`).test(s);
  if (normalized.includes(decimal) && normalized.includes(group)) {
    // Both present: the last one is the decimal separator, the other must group digits by three.
    const [dec, grp] = normalized.lastIndexOf(decimal) > normalized.lastIndexOf(group) ? [decimal, group] : [group, decimal];
    const at = normalized.lastIndexOf(dec);
    const integer = normalized.slice(0, at);
    if (!grouped(integer, grp)) return null;
    normalized = `${integer.split(grp).join("")}.${normalized.slice(at + 1)}`;
  } else if (normalized.includes(group)) {
    normalized = grouped(normalized, group) ? normalized.split(group).join("") : normalized.replace(group, ".");
  } else {
    normalized = normalized.replace(decimal, ".");
  }
  if (!/^-?\d+(\.\d{0,2})?$/.test(normalized)) return null;
  return roundCents(Number.parseFloat(normalized) * 100);
}

/** Margin ratio on net sale price (0.25 = 25 %). Null when the price is zero. */
export function marginRatio(saleCents: number, costCents: number): number | null {
  if (saleCents === 0) return null;
  return (saleCents - costCents) / saleCents;
}
